import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Image, Bot, GitCompare, MessageCircle, Settings, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AnimatePresence, motion } from "framer-motion";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import AIChatbot from "@/components/AIChatbot";

interface MenuDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MenuDrawer = ({ open, onOpenChange }: MenuDrawerProps) => {
  const navigate = useNavigate();
  const [showAIChat, setShowAIChat] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  const handleClose = () => {
    onOpenChange(false);
    setShowAIChat(false);
  };

  const handleNavigate = (path: string) => {
    handleClose();
    navigate(path);
  };

  return (
    <Drawer 
      open={open} 
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) setShowAIChat(false);
      }} 
      shouldScaleBackground={false}
    >
      <DrawerContent className={showAIChat ? "h-full bg-background/[0.78] backdrop-blur-lg border-none" : "bg-background border-t border-border rounded-t-2xl"}>
        <AnimatePresence mode="wait">
          {showAIChat ? (
            <AIChatbot key="chatbot" onBack={() => setShowAIChat(false)} />
          ) : (
            <motion.div
              key="menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="px-4 pb-8 pt-2"
            >
              <div className="w-12 h-1.5 rounded-full bg-muted mx-auto mb-4" />
              <div className="grid grid-cols-4 gap-3">
                {/* Confronta iPhone */}
                <motion.button 
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card transition-all duration-300 active:scale-90 group"
                  onClick={() => handleNavigate("/confronta-iphone")}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                >
                  <GitCompare className="w-7 h-7 text-teal-500 transition-all duration-300 group-hover:rotate-[-8deg]" />
                  <span className="text-xs font-medium text-teal-500">Confronta</span>
                </motion.button>

                {/* Sfondi */}
                <motion.button 
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card transition-all duration-300 active:scale-90 group"
                  onClick={() => handleNavigate("/sfondi")}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Image className="w-7 h-7 text-foreground transition-all duration-300 group-hover:rotate-[-8deg]" />
                  <span className="text-xs font-medium text-foreground">Sfondi</span>
                </motion.button>

                {/* Tecly AI */}
                <motion.button 
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card transition-all duration-300 active:scale-90 group"
                  onClick={() => setShowAIChat(true)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <Bot className="w-7 h-7 text-primary transition-all duration-300 group-hover:rotate-[-8deg]" />
                  <span className="text-xs font-medium bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">Tecly AI</span>
                </motion.button>

                {/* Messaggi */}
                <motion.button 
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card transition-all duration-300 active:scale-90 group"
                  onClick={() => handleNavigate("/messaggi")}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <MessageCircle className="w-7 h-7 text-green-500 transition-all duration-300 group-hover:rotate-[-8deg]" />
                  <span className="text-xs font-medium text-green-500">Messaggi</span>
                </motion.button>

                {/* Profilo */}
                {userId && (
                  <motion.button 
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card transition-all duration-300 active:scale-90 group"
                    onClick={() => handleNavigate(`/utente?id=${userId}`)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <User className="w-7 h-7 text-primary transition-all duration-300 group-hover:rotate-[-8deg]" />
                    <span className="text-xs font-medium text-primary">Profilo</span>
                  </motion.button>
                )}

                {/* Impostazioni */}
                <motion.button 
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-card transition-all duration-300 active:scale-90 group"
                  onClick={() => handleNavigate("/profile")}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Settings className="w-7 h-7 text-foreground transition-all duration-300 group-hover:rotate-[-8deg]" />
                  <span className="text-xs font-medium text-foreground">Impostazioni</span>
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DrawerContent>
    </Drawer>
  );
};